import React, { useState, useEffect, useRef } from "react";
import { useSync } from "../sync/SyncProvider";
import { T } from "../lib/globals";
import { getNativeSafeAsset, getSyncRunningDogIcon, isNativeCapacitorRuntime } from "../lib/platformAssets";
import { collectionForPriorityModule, OPERATIONAL_LANE_COLLECTIONS } from "../lib/syncPriority";

const SYNC_ONLINE_DOG_ICON  = getNativeSafeAsset("/icons/shared/blue-heeler-online.webp");
const SYNC_RUNNING_DOG_ICON = getSyncRunningDogIcon();
const SYNC_OFFLINE_DOG_ICON = getNativeSafeAsset("/icons/shared/blue-heeler-offline.webp");

function SyncStatusBar({ inline=false, activeModule=null, farmId="" }={}) {
  const ctx = useSync();
  const nativeSyncDogRef = useRef(null);
  const dogRefreshLastAtRef = useRef(0);
  const [dogRefreshing, setDogRefreshing] = useState(false);
  const [dogStatus, setDogStatus] = useState(null);
  // null | "checking" | "updated" | "no_change" | "busy" | "offline" | "not_available"
  // v3.8.03: dogRefreshing indica o revision_check/pull manual iniciado pelo usuário.
// eslint-disable-next-line no-unused-vars
  const [__pendingCount, setPendingCount] = useState(0);
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window === "undefined") return false;
    return window.innerWidth <= 768;
  });

  useEffect(() => {
    function update() { setPendingCount(c => c + 0); }
    window.addEventListener("fz:pending:changed", update);
    return () => window.removeEventListener("fz:pending:changed", update);
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;
    function handleResize() {
      setIsMobile(window.innerWidth <= 768);
    }
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const nativeRuntime = isNativeCapacitorRuntime();
  const nativeIOSRuntime = (() => {
    try {
      return !!(nativeRuntime && typeof window !== "undefined" && String(window.Capacitor?.getPlatform?.() || "").toLowerCase() === "ios");
    } catch (_) {
      return false;
    }
  })();
  const currentSyncing = !!ctx?.syncing || !!dogRefreshing;

  useEffect(() => {
    if (!nativeIOSRuntime) return undefined;
    const plugin = (() => {
      try {
        return window?.Capacitor?.Plugins?.TremboLiveActivities || window?.TremboLiveActivities || null;
      } catch (_) {
        return null;
      }
    })();
    const setVisible = (visible, frame = {}) => {
      try {
        if (!plugin?.setNativeSyncDogVisible) return;
        plugin.setNativeSyncDogVisible({
          visible: !!visible,
          x: Number(frame.x || 0),
          y: Number(frame.y || 0),
          width: Number(frame.width || 24),
          height: Number(frame.height || 24),
          duration: 0.45,
        }).catch?.(() => {});
      } catch (_) {}
    };
    if (!currentSyncing) {
      setVisible(false);
      return undefined;
    }
    let cancelled = false;
    const updateFrame = () => {
      if (cancelled) return;
      const el = nativeSyncDogRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      if (!rect || rect.width <= 0 || rect.height <= 0) return;
      setVisible(true, {
        x: rect.left,
        y: rect.top,
        width: rect.width,
        height: rect.height,
      });
    };
    const raf = window.requestAnimationFrame ? window.requestAnimationFrame(updateFrame) : null;
    const interval = window.setInterval(updateFrame, 1000);
    window.addEventListener("resize", updateFrame);
    window.addEventListener("scroll", updateFrame, true);
    document?.addEventListener?.("visibilitychange", updateFrame);
    return () => {
      cancelled = true;
      if (raf && window.cancelAnimationFrame) window.cancelAnimationFrame(raf);
      window.clearInterval(interval);
      window.removeEventListener("resize", updateFrame);
      window.removeEventListener("scroll", updateFrame, true);
      document?.removeEventListener?.("visibilitychange", updateFrame);
      setVisible(false);
    };
  }, [nativeIOSRuntime, currentSyncing, inline, isMobile]);

  if (!ctx) return null;
  const { online, syncing, lastSync, syncError, conflicts } = ctx;

  const pending = ctx._pendingCount || 0;
  const hasConflicts = conflicts.length > 0;

  // v3.7.00: classificação do módulo ativo para o refresh manual do cachorro.
  const activeCollection = collectionForPriorityModule(activeModule || "");
  const isDashboard = !activeModule;
  // Na lane técnica confirmada (orders/services/occurrences).
  const isInLane = activeCollection && OPERATIONAL_LANE_COLLECTIONS.has(activeCollection);
  // Tem mapeamento mas não está na lane técnica ainda (vet, inspections, futuros).
  const isMappedNotInLane = !isDashboard && !isInLane && activeCollection !== "";

  const handleDogClick = async () => {
    if (hasConflicts) return;
    if (!online) {
      setDogStatus("offline");
      setTimeout(() => setDogStatus(null), 2500);
      return;
    }
    if (syncing || dogRefreshing) {
      setDogStatus("busy");
      setTimeout(() => setDogStatus(null), 2000);
      return;
    }
    // Módulo com mapeamento mas fora da lane técnica → informar, sem sync.
    if (isMappedNotInLane) {
      setDogStatus("not_available");
      setTimeout(() => setDogStatus(null), 3000);
      return;
    }
    // Sem mapeamento (climate, meu_perfil) → silencioso.
    if (!isDashboard && !isInLane) return;
    // Cooldown 30s.
    const now = Date.now();
    if (now - dogRefreshLastAtRef.current < 30000) {
      setDogStatus("busy");
      setTimeout(() => setDogStatus(null), 2000);
      return;
    }
    dogRefreshLastAtRef.current = now;
    setDogRefreshing(true);
    setDogStatus("checking");
    try {
      // Dashboard → todas as coleções da lane em série.
      // Módulo na lane → só a collection ativa.
      const colsToSync = isDashboard
        ? Array.from(OPERATIONAL_LANE_COLLECTIONS)
        : [activeCollection];
      let anyChanged = false;
      for (const col of colsToSync) {
        const r = await ctx.checkAndSyncCollection?.(farmId, col).catch(() => null);
        if (r?.changed) anyChanged = true;
      }
      setDogStatus(anyChanged ? "updated" : "no_change");
    } catch (_) {
      setDogStatus("no_change");
    } finally {
      setDogRefreshing(false);
      setTimeout(() => setDogStatus(null), 3000);
    }
  };

  let bg, icon, label, anim, iconAnim, iconWrapStyle, iconNode = null;
  let visualShadow = "none";
  if (hasConflicts) {
    bg=T.red; icon="⚡"; label=`${conflicts.length} conflito(s)`;
    anim="fzPulse 1.8s infinite";
    iconAnim="fzPulse 1.4s infinite";
    iconWrapStyle={ textShadow:`0 0 12px ${T.red}55` };
    visualShadow = `0 4px 20px ${T.red}44`;
  } else if (!online) {
    bg=T.red; icon="📵"; anim="fzPulse 2.4s infinite";
    iconAnim="none";
    iconWrapStyle={ opacity:.96, display:"inline-flex", alignItems:"center", justifyContent:"center", filter:"drop-shadow(0 0 10px rgba(239,68,68,.22))" };
    visualShadow = `0 4px 20px ${T.red}33`;
    iconNode = <img src={SYNC_OFFLINE_DOG_ICON} alt="Offline" style={{width:24,height:24,display:"block",objectFit:"contain"}} />;
    label = pending > 0 ? `Offline · ${pending} aguardando` : "Offline";
  } else if (dogRefreshing) {
    // v3.8.03: refresh manual/revision_check iniciado pelo usuário.
    // O cachorro corre e o círculo fica com sombra verde enquanto o processo está ativo.
    bg=T.green; icon="🔄"; label="Verificando..."; anim="none";
    iconAnim="none";
    iconWrapStyle={
      opacity:.98,
      display:"inline-flex",
      alignItems:"center",
      justifyContent:"center",
      filter:"drop-shadow(0 0 12px rgba(34,197,94,.38))",
      transform:"translateZ(0)",
      willChange:nativeRuntime?"transform":"auto",
      backfaceVisibility:"hidden",
    };
    visualShadow = `0 4px 20px ${T.green}55`;
    iconNode = <img
      src={SYNC_RUNNING_DOG_ICON}
      alt="Verificando atualizações"
      style={{
        width:24,
        height:24,
        display:"block",
        objectFit:"contain",
        transform:"translateZ(0)",
        backfaceVisibility:"hidden",
        opacity:nativeIOSRuntime?0:1,
      }}
    />;
  } else if (syncing) {
    // v3.8.03: processo automático/scheduler usa laranja para diferenciar do clique manual.
    bg=T.yellow; icon="🔄"; label="Sincronizando..."; anim="none";
    iconAnim="none";
    iconWrapStyle={
      opacity:.98,
      display:"inline-flex",
      alignItems:"center",
      justifyContent:"center",
      filter:"drop-shadow(0 0 10px rgba(245,158,11,.34))",
      transform:"translateZ(0)",
      willChange:nativeRuntime?"transform":"auto",
      backfaceVisibility:"hidden",
    };
    visualShadow = `0 4px 20px ${T.yellow}55`;
    iconNode = <img
      src={SYNC_RUNNING_DOG_ICON}
      alt="Sincronizando"
      style={{
        width:24,
        height:24,
        display:"block",
        objectFit:"contain",
        transform:"translateZ(0)",
        backfaceVisibility:"hidden",
        opacity:nativeIOSRuntime?0:1,
      }}
    />;
  } else {
    // v3.8.03: aguardando/parado sem sombra, para deixar claro que não há processo ativo.
    bg=T.green; icon="☁️"; anim="none";
    iconAnim="none";
    iconWrapStyle={ opacity:.95, display:"inline-flex", alignItems:"center", justifyContent:"center" };
    iconNode = <img src={SYNC_ONLINE_DOG_ICON} alt="Online" style={{width:22,height:22,display:"block",objectFit:"contain"}} />;
    label = lastSync
      ? `Sync ${lastSync.toLocaleTimeString("pt-BR",{hour:"2-digit",minute:"2-digit"})}`
      : "Online";
  }

  const compact = !!inline;

  const dockStyle = inline
    ? {
        position:"relative",
        top:"auto",
        right:"auto",
        bottom:"auto",
        left:"auto",
        maxWidth:"none",
        zIndex:"auto",
        padding:"6px 8px",
        minWidth:34,
        justifyContent:"center",
        flexShrink:0,
      }
    : isMobile
    ? {
        top:"calc(env(safe-area-inset-top, 0px) + 10px)",
        right:12,
        bottom:"auto",
        maxWidth:"calc(100vw - 24px)",
        zIndex:180,
        padding:"6px 12px",
      }
    : {
        bottom:"calc(env(safe-area-inset-bottom, 0px) + 16px)",
        right:16,
        top:"auto",
        maxWidth:"none",
        zIndex:180,
        padding:"6px 14px",
      };

  return (
    <div style={{
      position:"fixed",
      display:"flex", alignItems:"center", gap:6,
      background:T.card, border:`1px solid ${bg}55`,
      borderRadius:20, fontSize:11,
      color:bg, fontWeight:600,
      boxShadow:visualShadow,
      cursor:((online&&!syncing&&!dogRefreshing) && (isDashboard || isInLane || isMappedNotInLane)) || hasConflicts?"pointer":"default",
      fontFamily:"'DM Sans',sans-serif",
      animation:anim,
      transition:"border-color .3s, color .3s, box-shadow .3s, transform .2s",
      backdropFilter:"blur(8px)",
      ...dockStyle,
    }}
      onClick={handleDogClick}
      title={
        hasConflicts                  ? `${conflicts.length} conflito(s)` :
        dogStatus === "checking"      ? "Verificando atualizações…" :
        dogStatus === "updated"       ? "✅ Atualizado!" :
        dogStatus === "no_change"     ? "Sem novidades" :
        dogStatus === "busy"          ? "Aguarde 30s…" :
        dogStatus === "offline"       ? "Sem conexão" :
        dogStatus === "not_available" ? "Atualização operacional ainda não disponível para este módulo" :
        isMappedNotInLane             ? "Atualização operacional ainda não disponível para este módulo" :
        !online                       ? (pending > 0 ? `${pending} alteração(ões) aguardando conexão` : "Sem conexão") :
        syncError                     ? syncError :
        "Clique para verificar atualizações"
      }>
      {!online && (
        <span style={{
          width:8, height:8, borderRadius:"50%",
          background:T.red, display:"inline-block",
          animation:"fzPulse 1s infinite",
          flexShrink:0,
        }}/>
      )}
      <span ref={currentSyncing ? nativeSyncDogRef : null} style={{fontSize:14, animation:iconAnim, transformOrigin:"50% 50%", ...iconWrapStyle}}>{iconNode || icon}</span>
      {!compact && <span style={{whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis"}}>{label}</span>}
      {pending > 0 && online && !syncing && (
        <span style={{
          background:T.yellow, color:"#000",
          borderRadius:"50%", width:compact?14:16, height:compact?14:16,
          display:"inline-flex", alignItems:"center",
          justifyContent:"center", fontSize:compact?8:9, fontWeight:700,
          flexShrink:0, marginLeft:compact?-4:0, marginTop:compact?-8:0,
          alignSelf:compact?"flex-start":"center"
        }}>{pending}</span>
      )}
      {syncError && !hasConflicts && (
        <span title={syncError} style={{color:T.red,marginLeft:2}}>⚠️</span>
      )}
    </div>
  );
}

export default SyncStatusBar;
export { SyncStatusBar };
